import { useColorModeValue, useTheme } from "@chakra-ui/react";
import { SingleDatepicker } from "chakra-dayzed-datepicker";
import { CalendarIcon } from "@chakra-ui/icons";
import { PropsWithChildren, useMemo } from "react";
import { DEFAULT_DATE_FORMAT } from "definitions/constants";
import clsx from "clsx";
import parseStrictDate from "helpers/time/parseStrictDate";

type BaseDatePickerProps = {
  name: string;
  id?: string;
  disabled?: boolean;
  minDate?: Date;
  defaultIsOpen?: boolean;
  usePortal?: boolean;
  receiveDate?: boolean;
  dateFormat?: string;
  variant?: string;
  stylingClassName?: string;
  triggerButtonProps?: Record<string, any>;
  isClearable?: boolean;
  includeIcon?: boolean;
  onDateChange: (date: Date) => void;
  hasError?: boolean;
  value: string | Date | undefined;
  defaultValue?: string | Date;
} &
  PropsWithChildren;

export default function BaseDatePicker({
  value,
  name,
  disabled,
  defaultValue,
  minDate,
  defaultIsOpen = false,
  usePortal = false,
  receiveDate = false,
  dateFormat = DEFAULT_DATE_FORMAT,
  children,
  stylingClassName,
  onDateChange,
  isClearable,
  variant = "unstyled",
  triggerButtonProps = {},
  includeIcon = true,
  hasError = false,
  ...rest
}: BaseDatePickerProps) {
  const theme = useTheme();

  const iconColorScheme = useColorModeValue(theme.colors.gray[600], theme.colors.gray[300]);

  const date = useMemo(() => {
    const dateString = value ?? defaultValue;
    if (!dateString) return undefined;
    if (typeof dateString === "string") {
      return receiveDate ? parseStrictDate(dateString) : new Date(dateString);
    }
    return dateString;
  }, [value, defaultValue, receiveDate]);

  const datePickerClassName = date ? "has-value" : "";
  const hasErrorClassName = hasError ? "has-error" : "";

  return (
    <SingleDatepicker
      id={name}
      name={name}
      usePortal={usePortal}
      date={date}
      minDate={minDate}
      onDateChange={(val) => onDateChange(val)}
      disabled={disabled}
      configs={{ dateFormat }}
      isClearable={isClearable}
      defaultIsOpen={defaultIsOpen}
      propsConfigs={{
        triggerBtnProps: {
          width: "100%",
          justifyContent: "start",
          color: "fg.muted",
          fontSize: "md",
          fontWeight: "normal",
          variant,
          iconSpacing: "auto",
          className: clsx(stylingClassName, datePickerClassName, hasErrorClassName),
          rightIcon: includeIcon ? <CalendarIcon color={iconColorScheme} /> : undefined,
          ...triggerButtonProps,
        },
        dayOfMonthBtnProps: {
          todayBtnProps: {
            background: "brand.400",
          },
        },
      }}
      {...rest}
    >
      {children && ((_value) => children)}
    </SingleDatepicker>
  );
}
